import { useState } from "react";
import SkeletonBase from "newui/components/Skeleton";
import { usePanelMemberStatus } from "../../../../../hooks/usePanelScoringQueries";
import { ConflictReviewModal } from "../../../../ConflictReviewModal";
import { MemberRow } from "./MemberRow";
import type { PanelMemberListMode } from "./types";
import type { PanelMemberWithProgress } from "../../../../../types/panel.type";

interface InitialPanelMemberListViewProps {
	disclaimerEnabled?: boolean;
	evalId: number;
	mode?: PanelMemberListMode;
	rfxId: number;
}

type ConflictModalState = {
	member: PanelMemberWithProgress;
	mode: "review" | "view";
} | null;

export const InitialPanelMemberListView: React.FC<InitialPanelMemberListViewProps> = ({
	rfxId,
	evalId,
	mode = "initial",
	disclaimerEnabled = false,
}) => {
	const { data: members, isLoading, error } = usePanelMemberStatus(rfxId, evalId, mode);
	const [conflictModal, setConflictModal] = useState<ConflictModalState>(null);

	if (isLoading) {
		return (
			<div className="space-y-2" data-testid="panel-member-list-loading">
				{[1, 2, 3].map((i) => (
					<SkeletonBase key={i} startColor="lightgray" width="100%" height="48px" className="!rounded-lg" />
				))}
			</div>
		);
	}

	if (error) {
		return (
			<p className="text-sm text-red-600" data-testid="panel-member-list-error">
				Failed to load panel members.
			</p>
		);
	}

	const isFinal = mode === "final";
	const activeMembers = (members ?? []).filter((m) => {
		if (m.is_removed) {
			return false;
		}
		if (isFinal && m.status === "declined") {
			return false;
		}
		// PRO-664: the RFx Lead's member row is auto-created server-side so scores can be
		// attached; it does not mean they joined the panel. Only list the lead once they
		// have explicitly opted into scoring.
		if (m.role === "rfxLead" && m.is_scoring !== true) {
			return false;
		}
		return true;
	});

	if (activeMembers.length === 0) {
		return (
			<div
				className="rounded-lg border border-dashed border-gray-300 p-8 text-center text-gray-400 text-sm"
				data-testid="panel-member-list-empty"
			>
				No panel members have been added to this evaluation.
			</div>
		);
	}

	const submittedCount = activeMembers.filter((m) =>
		isFinal ? m.final_status === "submitted" : m.status === "submitted"
	).length;
	const hasNonLeadMembers = activeMembers.some((m) => m.role !== "rfxLead");

	return (
		<div data-testid="panel-member-list">
			<div className="mb-3 flex items-center justify-between">
				<h4 className="text-sm font-medium text-gray-700">
					Panel Members
				</h4>
				<span className="text-xs text-gray-500">
					{submittedCount} of {activeMembers.length} submitted
				</span>
			</div>
			<div className="w-full overflow-visible">
				<table className="w-full min-w-[600px] border-collapse text-sm">
					<thead>
						<tr className="border-b border-gray-200 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
							<th className="px-3 py-3">Member</th>
							<th className="px-3 py-3">Role</th>
							<th className="px-3 py-3">Status</th>
							{disclaimerEnabled && (
								<th className="px-3 py-3">Disclaimer</th>
							)}
							<th className="px-3 py-3">Progress</th>
							<th className="px-3 py-3">Submitted</th>
							{hasNonLeadMembers && (
								<th className="px-3 py-3 w-12">Actions</th>
							)}
						</tr>
					</thead>
					<tbody>
						{activeMembers.map((member) => (
							<MemberRow
								key={member.id}
								member={member}
								rfxId={rfxId}
								evalId={evalId}
								showActions={hasNonLeadMembers}
								showDisclaimer={disclaimerEnabled}
								mode={mode}
								onReviewConflict={
									member.disclaimer_status === "conflict"
										? (m) => setConflictModal({ member: m, mode: "review" })
										: undefined
								}
								onViewConflict={
									member.disclaimer_status === "reviewed"
										? (m) => setConflictModal({ member: m, mode: "view" })
										: undefined
								}
							/>
						))}
					</tbody>
				</table>
			</div>

			{conflictModal && (
				<ConflictReviewModal
					isOpen
					onClose={() => setConflictModal(null)}
					rfxId={rfxId}
					evalId={evalId}
					memberId={conflictModal.member.id}
					memberName={conflictModal.member.name || conflictModal.member.email}
					memberEmail={conflictModal.member.email}
					conflictDetails={conflictModal.member.disclaimer_conflict_details ?? ""}
					mode={conflictModal.mode}
					reviewAction={conflictModal.member.disclaimer_review_action}
					reviewNotes={conflictModal.member.disclaimer_review_notes}
					reviewedAt={conflictModal.member.disclaimer_reviewed_at}
				/>
			)}
		</div>
	);
};
